import { SelectPicker } from "rsuite";
import AppDrawer from "../../Drawers/AppDrawer";
import RequiredTitle from "../../../elements/RequiredTitle";
import { Alert, Button, Input } from "reactstrap";
import { useEffect, useState } from "react";
import { DrawerLoader } from "../../Drawers/ShareRecordDrawer/ShareRecordDrawer";
import useCreateSharedScreen from "./useCreateSharedScreen";
import CopyGroup from "../../Modals/CopyGroup";
import { AppConfirm } from "../../../utils/PopupUtil";
import useUnshareScreen from "./useUnshareScreen";
import AppSpinner from "../../../elements/AppSpinner";
import useUpdateSharedScreen from "./useUpdateSharedScreen";
import { useAtomValue } from "jotai";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import useSharedScreenInTenant from "./useSharedScreenInTenant";
import useView from "../../../api/api-hooks/views/useView";

const authOptions = [
  { label: "Password Protected", value: "Password" },
  { label: "Require Logged In User", value: "GC User" },
  { label: "None", value: "None" },
];

export type ShareAuthType = "Password" | "GC User" | "None";

interface Props {
  open: boolean;
  onClose: () => void;
  viewID: string | undefined;
  recordID: string | undefined;
  recordModule: string | undefined;
  parentViewID?: string;
  pageID: string | undefined;
  documentID: string | undefined;
}

// Supports sharing, Page, View, Subview.
export default function ShareScreenDrawer({
  open,
  onClose,
  viewID,
  recordID,
  recordModule,
  parentViewID,
  pageID,
  documentID,
}: Props) {
  const { view, viewLoading } = useView({ viewID, pageID });

  const docID = getDocumentID();

  const {
    loading: dataLoading,
    exists,
    authentication,
    password: existingPassword,
    sharedScreenID: existingID,
  } = useSharedScreenInTenant({ viewID, recordID, pageID, documentID: docID });

  const { createSharedScreen, creatingScreen } = useCreateSharedScreen();
  const { unshareScreen, unsharingScreen } = useUnshareScreen();
  const { updateSharedScreen, updatingScreen } = useUpdateSharedScreen();
  const tenantID = useAtomValue(tenantIDAtom);

  const loading = viewID && pageID ? viewLoading || dataLoading : dataLoading;

  const [sharedScreenID, setSharedScreenID] = useState("");
  const [auth, setAuth] = useState<ShareAuthType | "">("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState(false);

  const shareName = docID ? "document" : viewID ? "view" : "page";
  const shareNameCapital =
    shareName.charAt(0).toUpperCase() + shareName.slice(1);

  useEffect(() => {
    if (!exists) return;

    setAuth(authentication);
    setPassword(existingPassword);
    setSharedScreenID(existingID);
  }, [exists, authentication, existingPassword, existingID]);

  let Body;

  if (loading) Body = <DrawerLoader />;

  Body = (
    <>
      {exists && (
        <>
          <div className="mt--4">
            <CopyGroup
              name={`Share ${shareNameCapital} Link`}
              description={`Copy and paste this link to share the ${shareName}.`}
              link={`${
                window.location.origin
              }/shared-${shareName}/${tenantID}/${sharedScreenID}${
                recordID ? `/${recordID}` : ""
              }`}
            />
          </div>
          <hr />
        </>
      )}
      <RequiredTitle title={`${shareNameCapital} Authentication`} />
      <SelectPicker
        data={authOptions}
        value={auth}
        block
        placeholder={`Select ${shareNameCapital} Authentication`}
        searchable={false}
        cleanable={false}
        onChange={(val) => setAuth(val as ShareAuthType)}
      />
      {auth === "Password" && (
        <>
          <RequiredTitle title="Password" className="mt-4" />
          <Input
            bsSize="sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
          />
        </>
      )}
      <div className="d-flex align-items-center mt-4">
        {exists && (
          <Button onClick={handleUnshareView}>
            {unsharingScreen ? (
              <AppSpinner color="primary" />
            ) : (
              `Unshare ${shareNameCapital}`
            )}
          </Button>
        )}
        <Button
          color="primary"
          disabled={
            !auth ||
            (auth === "Password" && !password) ||
            creatingScreen ||
            updatingScreen
          }
          onClick={exists ? handleUpdateSharedView : handleShareView}
        >
          {creatingScreen || updatingScreen ? (
            <AppSpinner color="white" />
          ) : exists ? (
            `Update Shared ${shareNameCapital}`
          ) : (
            `Share ${shareNameCapital}`
          )}
        </Button>
      </div>
    </>
  );

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle={`Share ${shareNameCapital}`}
      headerDescription={`Share this ${shareName} as a public URL`}
      body={
        <>
          {Body}
          {error && (
            <Alert className="mt-4" color="danger">
              {error}
            </Alert>
          )}
          {updateSuccess && (
            <Alert className="mt-4">
              Successfully updated shared {shareName} settings!
            </Alert>
          )}
        </>
      }
    />
  );

  function getDocumentID() {
    if (documentID) return documentID;

    if (!view || view?.Type !== "Document") return undefined;

    return view?.DocumentPageType === "Multiple" ? undefined : view?.Record?.id;
  }

  function handleShareView() {
    if (!auth) return;

    setError("");
    setUpdateSuccess(false);

    createSharedScreen({
      viewID: viewID,
      authentication: auth,
      password: password,
      recordID: recordID,
      recordModule: recordModule,
      onSuccess: setSharedScreenID,
      onError: setError,
      parentViewID: parentViewID,
      parentPageID: pageID,
      documentID: docID,
    });
  }

  async function handleUnshareView() {
    const result = await AppConfirm({
      title: `Unshare ${shareNameCapital}`,
      text: `Are you sure you want to unshare this ${shareName}?`,
      confirmButtonText: "Yes",
    });

    if (!result.isConfirmed) return;

    setError("");
    setUpdateSuccess(false);

    if (!documentID && !viewID && !pageID) return;

    unshareScreen({
      viewID: viewID,
      recordID: recordID,
      onError: setError,
      onSuccess: resetState,
      parentViewID: parentViewID,
      parentPageID: pageID,
      documentID: docID,
    });
  }

  async function handleUpdateSharedView() {
    setError("");
    setUpdateSuccess(false);

    if (!documentID && !viewID && !pageID) return;

    updateSharedScreen({
      viewID: viewID,
      pageID: pageID,
      documentID: docID,
      authentication: auth as ShareAuthType,
      password: password,
      recordID: recordID,
      onError: setError,
      onSuccess: () => setUpdateSuccess(true),
    });
  }

  function resetState() {
    setAuth("");
    setPassword("");
    setError("");
  }

  function handleClose() {
    resetState();
    onClose();
  }
}
